// CANDLE.AI translation dictionaries
import { phase2 } from "./translations_phase2";

const base = {
  en: {
    // Brand
    brand_name: "CANDLE.AI",
    brand_subtagline: "AI-powered chart analysis for serious traders.",
    // Nav
    nav_home: "Home",
    nav_pricing: "Pricing",
    nav_dashboard: "Dashboard",
    nav_login: "Login",
    nav_signup: "Get Access",
    nav_logout: "Logout",
    nav_billing: "Billing",
    nav_settings: "Settings",
    // Landing hero
    hero_status: "SYS // CLAUDE_SONNET_4.5 // LIVE",
    hero_title_1: "Upload a chart.",
    hero_title_2: "Get the trade.",
    hero_desc: "Drag any candlestick screenshot — stocks, crypto, forex — and our AI returns directional bias, confidence, detected patterns, and precise entry / TP / stop-loss levels in seconds.",
    hero_cta_start: "Start Analyzing — Free",
    hero_cta_how: "How it works",
    stat_model: "MODEL",
    stat_latency: "LATENCY",
    stat_output: "OUTPUT",
    stat_markets: "MARKETS",
    // How it works
    how_label: "PROTOCOL",
    how_title: "Three steps. Zero noise.",
    how_1_title: "Upload Chart",
    how_1_body: "Drag any PNG/JPEG screenshot from TradingView, Robinhood, or your terminal.",
    how_2_title: "Claude Vision",
    how_2_body: "Sonnet 4.5 reads candles, volume, indicators and identifies the structure.",
    how_3_title: "Get Levels",
    how_3_body: "Receive direction, confidence, patterns, and precise entry / TP / stop-loss.",
    // Capabilities
    caps_label: "CAPABILITIES",
    caps_title_1: "Built for traders who",
    caps_title_2: "don't trust vibes.",
    caps_body: "We don't predict the future. We give you a structured second opinion — the same checklist a senior technical analyst runs, in seconds, on any chart you can screenshot.",
    cap_pattern_t: "Pattern Detection",
    cap_pattern_d: "Flags, double tops/bottoms, head & shoulders, wedges, triangles.",
    cap_levels_t: "Precise Levels",
    cap_levels_d: "Entry, TP1, TP2, Stop-Loss with rationale tied to chart structure.",
    cap_indicator_t: "Indicator Read",
    cap_indicator_d: "RSI, MACD, volume, moving averages — interpreted holistically.",
    cap_risk_t: "Risk Honesty",
    cap_risk_d: "Confidence score and risk note on every call. No false promises.",
    // Social proof
    social_label: "TRUSTED BY TRADERS",
    social_title: "Numbers don't lie.",
    social_1_val: "12,400+",
    social_1_lbl: "Charts analyzed",
    social_2_val: "94%",
    social_2_lbl: "Accuracy on direction",
    social_3_val: "40+",
    social_3_lbl: "Countries",
    social_4_val: "6.2s",
    social_4_lbl: "Avg. analysis time",
    // FAQ
    faq_label: "FAQ",
    faq_title: "Common questions.",
    faq_q1: "How accurate is the analysis?",
    faq_a1: "Our model identifies trend direction correctly in ~94% of clean setups. Lower confidence (<55%) signals ambiguous or weak structures — we tell you when.",
    faq_q2: "Which markets are supported?",
    faq_a2: "Anything with a candlestick chart: equities, ETFs, forex pairs, crypto, indices, futures. The model is market-agnostic.",
    faq_q3: "Is this financial advice?",
    faq_a3: "No. CANDLE.AI provides analytical output only. Always do your own research. Trading carries risk of loss.",
    faq_q4: "Can I use it on any timeframe?",
    faq_a4: "Yes. Pick the trading style (Scalping / Day / Swing / Long-Term) that matches your timeframe and the analysis adapts accordingly.",
    faq_q5: "Do you store my uploaded charts?",
    faq_a5: "Yes — only in your private history so you can revisit past analyses. They are never shared.",
    faq_q6: "Can I cancel my plan anytime?",
    faq_a6: "Absolutely. Cancel in a click from your billing page — no questions asked.",
    // CTA
    cta_title_1: "Stop guessing.",
    cta_title_2: "Start scanning.",
    cta_desc: "Free to start. Upload your first chart in under 30 seconds.",
    cta_btn: "Create Free Account →",
    // Footer
    footer_disclaimer: "CANDLE.AI © 2026 — Not financial advice",
    footer_privacy: "Privacy",
    footer_terms: "Terms",
    footer_contact: "Contact",
    // Login
    login_session: "SESSION // INIT",
    login_title_1: "Access the",
    login_title_2: "analyst terminal.",
    login_desc: "Authenticate to upload charts, get AI-graded trade setups, and review your full analysis history.",
    tab_login: "Login",
    tab_signup: "Sign Up",
    google_btn: "Continue with Google",
    or_divider: "OR",
    field_email: "Email",
    field_password: "Password",
    field_name: "Name",
    placeholder_email: "trader@market.com",
    placeholder_password: "••••••••",
    placeholder_password_signup: "Min 6 characters",
    placeholder_name: "Your name",
    btn_authenticate: "Authenticate →",
    btn_create_account: "Create Account →",
    login_legal: "By continuing you acknowledge that CANDLE.AI provides AI-generated technical analysis only — not financial advice. Trade at your own risk.",
    toast_welcome: "Authenticated. Welcome back.",
    toast_account_created: "Account created.",
    toast_login_failed: "Login failed",
    toast_signup_failed: "Signup failed",
    // Dashboard
    dashboard_terminal: "TERMINAL //",
    welcome: "Welcome",
    trader_default: "Trader",
    style_label: "TRADING STYLE",
    style_scalping: "Scalping",
    style_day: "Day Trading",
    style_swing: "Swing",
    style_long: "Long Term",
    style_scalping_tf: "1m · 5m · 15m",
    style_day_tf: "15m · 1h · 4h",
    style_swing_tf: "4h · 1D · 1W",
    style_long_tf: "1W · 1M",
    drop_title: "Drop your chart screenshot",
    drop_desc: "PNG, JPEG or WEBP — up to 8MB. Best results with full candlestick charts.",
    browse_btn: "Browse File",
    clear_btn: "Clear",
    ctx_label: "CONTEXT (OPTIONAL)",
    field_symbol: "Symbol",
    field_timeframe: "Timeframe",
    field_notes: "Notes",
    placeholder_symbol: "e.g. AAPL · BTCUSD",
    placeholder_timeframe: "e.g. 1D · 4H · 15m",
    placeholder_notes: "Anything to consider...",
    last_used: "Last used",
    analyze_btn: "Run AI Analysis →",
    scanning: "SCANNING...",
    risk_warning: "AI output is informational only. Always do your own research before trading.",
    history_label: "SESSION_LOG // RECENT ANALYSES",
    history_entries: "entries",
    history_empty: "No analyses yet. Upload your first chart above.",
    toast_upload_first: "Upload a chart image first",
    toast_only_image: "Please upload an image file",
    toast_too_big: "Image must be under 8MB",
    toast_analysis_complete: "Analysis complete",
    toast_analysis_failed: "Analysis failed",
    toast_delete_failed: "Failed to delete",
    // Analysis detail
    back_btn: "Back",
    prediction_label: "PREDICTION",
    confidence_label: "CONFIDENCE",
    market_structure_label: "MARKET_STRUCTURE",
    levels_label: "TRADE_LEVELS",
    key_levels_label: "KEY_LEVELS",
    support: "Support",
    resistance: "Resistance",
    entry: "Entry",
    tp1: "Take Profit 1",
    tp2: "Take Profit 2",
    stop_loss: "Stop Loss",
    rr_label: "RISK / REWARD",
    invalidation_label: "INVALIDATION",
    summary_label: "SUMMARY",
    patterns_label: "PATTERNS_DETECTED",
    candle_patterns_label: "CANDLESTICK_PATTERNS",
    style_notes_label: "TRADING_STYLE_NOTES",
    none_identified: "None identified.",
    no_summary: "—",
    disclaimer: "CANDLE.AI is an analytical tool only. This is not financial advice. Always do your own research before investing.",
    direction_BULLISH: "BULLISH",
    direction_BEARISH: "BEARISH",
    direction_NEUTRAL: "NEUTRAL",
  },
  fr: {
    brand_name: "CANDLE.AI",
    brand_subtagline: "Analyse de graphiques par IA pour traders sérieux.",
    nav_home: "Accueil",
    nav_pricing: "Tarifs",
    nav_dashboard: "Tableau de bord",
    nav_login: "Connexion",
    nav_signup: "Accéder",
    nav_logout: "Déconnexion",
    nav_billing: "Facturation",
    nav_settings: "Paramètres",
    hero_status: "SYS // CLAUDE_SONNET_4.5 // EN LIGNE",
    hero_title_1: "Uploadez un graphique.",
    hero_title_2: "Obtenez le trade.",
    hero_desc: "Glissez n'importe quel screenshot de chandeliers — actions, crypto, forex — notre IA renvoie biais directionnel, confiance, patterns détectés et niveaux d'entrée / TP / stop-loss en quelques secondes.",
    hero_cta_start: "Commencer l'analyse — Gratuit",
    hero_cta_how: "Comment ça marche",
    stat_model: "MODÈLE",
    stat_latency: "LATENCE",
    stat_output: "SORTIE",
    stat_markets: "MARCHÉS",
    how_label: "PROTOCOLE",
    how_title: "Trois étapes. Zéro bruit.",
    how_1_title: "Uploadez",
    how_1_body: "Glissez un screenshot PNG/JPEG depuis TradingView, Robinhood ou votre terminal.",
    how_2_title: "Claude Vision",
    how_2_body: "Sonnet 4.5 lit chandeliers, volume, indicateurs et identifie la structure.",
    how_3_title: "Recevez vos niveaux",
    how_3_body: "Direction, confiance, patterns, et niveaux précis d'entrée / TP / stop-loss.",
    caps_label: "CAPACITÉS",
    caps_title_1: "Conçu pour les traders qui",
    caps_title_2: "ne font pas confiance aux vibes.",
    caps_body: "On ne prédit pas l'avenir. On vous donne une seconde opinion structurée — la même checklist qu'un analyste senior exécuterait, en quelques secondes, sur n'importe quel graphique.",
    cap_pattern_t: "Détection de Patterns",
    cap_pattern_d: "Drapeaux, double sommets/creux, tête-épaules, biseaux, triangles.",
    cap_levels_t: "Niveaux Précis",
    cap_levels_d: "Entry, TP1, TP2, Stop-Loss avec justification basée sur la structure.",
    cap_indicator_t: "Lecture d'Indicateurs",
    cap_indicator_d: "RSI, MACD, volume, moyennes mobiles — interprétés globalement.",
    cap_risk_t: "Honnêteté sur le Risque",
    cap_risk_d: "Score de confiance et avertissement à chaque appel. Aucune fausse promesse.",
    social_label: "ADOPTÉ PAR LES TRADERS",
    social_title: "Les chiffres ne mentent pas.",
    social_1_val: "12 400+",
    social_1_lbl: "Graphiques analysés",
    social_2_val: "94%",
    social_2_lbl: "Précision directionnelle",
    social_3_val: "40+",
    social_3_lbl: "Pays",
    social_4_val: "6,2s",
    social_4_lbl: "Temps moyen d'analyse",
    faq_label: "FAQ",
    faq_title: "Questions fréquentes.",
    faq_q1: "Quelle est la précision de l'analyse ?",
    faq_a1: "Notre modèle identifie correctement la direction dans ~94% des setups clairs. Une confiance basse (<55%) signale une structure ambiguë — on vous le dit.",
    faq_q2: "Quels marchés sont supportés ?",
    faq_a2: "Tout ce qui a un graphique en chandeliers : actions, ETF, paires forex, crypto, indices, futures. Le modèle est agnostique.",
    faq_q3: "Est-ce un conseil financier ?",
    faq_a3: "Non. CANDLE.AI fournit uniquement une sortie analytique. Faites toujours vos propres recherches. Le trading comporte un risque de perte.",
    faq_q4: "Puis-je utiliser n'importe quel timeframe ?",
    faq_a4: "Oui. Choisissez le style de trading (Scalping / Day / Swing / Long Terme) correspondant et l'analyse s'adapte.",
    faq_q5: "Stockez-vous mes graphiques ?",
    faq_a5: "Oui — uniquement dans votre historique privé pour pouvoir y revenir. Jamais partagé.",
    faq_q6: "Puis-je annuler mon abonnement ?",
    faq_a6: "Absolument. Annulation en un clic depuis votre page facturation, sans question.",
    cta_title_1: "Arrêtez de deviner.",
    cta_title_2: "Commencez à scanner.",
    cta_desc: "Gratuit. Uploadez votre premier graphique en moins de 30 secondes.",
    cta_btn: "Créer un compte gratuit →",
    footer_disclaimer: "CANDLE.AI © 2026 — Pas un conseil financier",
    footer_privacy: "Confidentialité",
    footer_terms: "Conditions",
    footer_contact: "Contact",
    login_session: "SESSION // INIT",
    login_title_1: "Accédez au",
    login_title_2: "terminal d'analyse.",
    login_desc: "Authentifiez-vous pour uploader des graphiques, obtenir des setups gradés par IA, et consulter votre historique complet.",
    tab_login: "Connexion",
    tab_signup: "Inscription",
    google_btn: "Continuer avec Google",
    or_divider: "OU",
    field_email: "Email",
    field_password: "Mot de passe",
    field_name: "Nom",
    placeholder_email: "trader@marche.com",
    placeholder_password: "••••••••",
    placeholder_password_signup: "Min 6 caractères",
    placeholder_name: "Votre nom",
    btn_authenticate: "S'authentifier →",
    btn_create_account: "Créer un compte →",
    login_legal: "En continuant vous reconnaissez que CANDLE.AI fournit une analyse technique générée par IA uniquement — pas un conseil financier. Tradez à vos risques.",
    toast_welcome: "Authentifié. Bon retour.",
    toast_account_created: "Compte créé.",
    toast_login_failed: "Échec de connexion",
    toast_signup_failed: "Échec d'inscription",
    dashboard_terminal: "TERMINAL //",
    welcome: "Bienvenue",
    trader_default: "Trader",
    style_label: "STYLE DE TRADING",
    style_scalping: "Scalping",
    style_day: "Day Trading",
    style_swing: "Swing",
    style_long: "Long Terme",
    style_scalping_tf: "1m · 5m · 15m",
    style_day_tf: "15m · 1h · 4h",
    style_swing_tf: "4h · 1J · 1S",
    style_long_tf: "1S · 1M",
    drop_title: "Déposez votre screenshot de graphique",
    drop_desc: "PNG, JPEG ou WEBP — jusqu'à 8MB. Meilleurs résultats avec un graphique en chandeliers complet.",
    browse_btn: "Parcourir",
    clear_btn: "Effacer",
    ctx_label: "CONTEXTE (OPTIONNEL)",
    field_symbol: "Devise",
    field_timeframe: "Timeframe",
    field_notes: "Notes",
    placeholder_symbol: "ex. AAPL · BTCUSD",
    placeholder_timeframe: "ex. 1J · 4H · 15m",
    placeholder_notes: "Quelque chose à considérer...",
    last_used: "Dernier",
    analyze_btn: "Lancer l'analyse IA →",
    scanning: "ANALYSE...",
    risk_warning: "La sortie IA est informative uniquement. Faites toujours vos propres recherches avant de trader.",
    history_label: "JOURNAL // ANALYSES RÉCENTES",
    history_entries: "entrées",
    history_empty: "Aucune analyse. Uploadez votre premier graphique ci-dessus.",
    toast_upload_first: "Uploadez un graphique d'abord",
    toast_only_image: "Veuillez uploader une image",
    toast_too_big: "L'image doit faire moins de 8MB",
    toast_analysis_complete: "Analyse terminée",
    toast_analysis_failed: "Échec de l'analyse",
    toast_delete_failed: "Échec de suppression",
    back_btn: "Retour",
    prediction_label: "PRÉDICTION",
    confidence_label: "CONFIANCE",
    market_structure_label: "STRUCTURE_MARCHÉ",
    levels_label: "NIVEAUX_TRADE",
    key_levels_label: "NIVEAUX_CLÉS",
    support: "Support",
    resistance: "Résistance",
    entry: "Entrée",
    tp1: "Take Profit 1",
    tp2: "Take Profit 2",
    stop_loss: "Stop Loss",
    rr_label: "RISQUE / RÉCOMPENSE",
    invalidation_label: "INVALIDATION",
    summary_label: "RÉSUMÉ",
    patterns_label: "PATTERNS_DÉTECTÉS",
    candle_patterns_label: "PATTERNS_CHANDELIERS",
    style_notes_label: "NOTES_STYLE_TRADING",
    none_identified: "Aucun identifié.",
    no_summary: "—",
    disclaimer: "CANDLE.AI est un outil analytique uniquement. Ce n'est pas un conseil financier. Faites toujours vos propres recherches avant d'investir.",
    direction_BULLISH: "HAUSSIER",
    direction_BEARISH: "BAISSIER",
    direction_NEUTRAL: "NEUTRE",
  },
};

// Merge Phase 2 keys
export const dicts = {
  en: { ...base.en, ...phase2.en },
  fr: { ...base.fr, ...phase2.fr },
};
